import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  GraduationCap,
  Users,
  CalendarX,
  Trophy,
  UserCog,
  History,
  Lock,
  ArrowRightLeft,
  LogOut,
  Menu,
  X,
  ArrowLeft,
  Calendar,
  UserCheck,
  LayoutGrid,
  Layers,
  Link2,
  Bell,
  HeartHandshake,
  ClipboardList,
  PanelLeftClose,
  PanelLeftOpen,
  Clock,
  Contact,
  ShieldCheck,
  ListChecks,
  FileSignature,
  PackageCheck,
  BarChart3,
  Server,
  Tag,
  Flame,
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  IdCard,
  Shield,
  Boxes,
  CalendarCheck,
  BookOpen,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useInstitution } from '../contexts/InstitutionContext';
import NotificationBell from './NotificationBell';
import { API_URL } from '../api/config';

// Cada rota em App.js envolve sua tela com <Layout><Página/></Layout> — ou
// seja, TODA navegação desmonta o <Layout> da rota antiga e monta um novo
// (não é um layout persistente com <Outlet/>). Isso reseta a rolagem do
// menu pra 0 sozinho a cada clique, porque o <nav> é um elemento de DOM
// novo. Pra sobreviver a esse remount sem reescrever todo o roteamento,
// guarda a última posição de rolagem numa variável de MÓDULO (fora do
// componente — sobrevive porque o módulo só é carregado uma vez por sessão
// de aba, mesmo com o componente remontando) e restaura assim que o próximo
// <nav> aparece.
let ultimaRolagemMenu = 0;

// Casca visual (sidebar + header) usada por quase toda tela logada — ver as
// rotas em App.js, a maioria envolve sua tela com <Layout>...</Layout>.
function Layout({ children }) {
  const { institutionName, selectInstitution, permissoes } = useInstitution();
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const navRef = useRef(null);
  // Restaura a rolagem salva assim que este <nav> (novo a cada navegação,
  // ver comentário de `ultimaRolagemMenu` acima) aparece no DOM — roda antes
  // do navegador pintar a tela, então o usuário nunca chega a ver o "pulo"
  // pro topo.
  useLayoutEffect(() => {
    if (navRef.current) navRef.current.scrollTop = ultimaRolagemMenu;
  }, []);
  const [menuAberto, setMenuAberto] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );
  // Menu reduzido (só ícones) — só faz sentido no modo PC (aside fixo, md:static);
  // no mobile o menu é sempre um overlay em tela cheia, então essa preferência
  // não se aplica lá (ver classes `md:` usadas junto de menuColapsado abaixo).
  const [menuColapsado, setMenuColapsado] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('menuColapsado') === 'true',
  );

  // Também acompanha se a tela atual é "desktop" (>= md) pra impedir que o
  // menu colapsado (só ícones) vaze pro mobile: `menuColapsado` fica salvo no
  // localStorage e sobrevive entre sessões/dispositivos do mesmo navegador —
  // sem isso, um usuário que colapsou o menu no PC via essa preferência
  // continuava "colapsado" ao abrir no celular, e MenuGrupo (mais abaixo)
  // decidia entre accordion inline e popover flutuante só olhando pra esse
  // valor puro, sem saber que no mobile a barra lateral já é sempre em tela
  // cheia — o popover (pensado pra abrir do lado de um ícone estreito)
  // aparecia fora da largura visível, exigindo rolagem horizontal pra ver o
  // submenu.
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true,
  );
  useEffect(() => {
    const media = window.matchMedia('(min-width: 768px)');
    const handleChange = (e) => {
      setMenuAberto(e.matches);
      setIsDesktop(e.matches);
    };
    setMenuAberto(media.matches);
    setIsDesktop(media.matches);
    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, []);
  const menuColapsadoEfetivo = menuColapsado && isDesktop;

  const alternarMenuColapsado = () => {
    setMenuColapsado((prev) => {
      localStorage.setItem('menuColapsado', String(!prev));
      return !prev;
    });
  };

  // Grupos de menu com submenu (ver `children` em menuItemsBase) — cada um
  // abre sozinho quando a rota atual é uma das telas daquele grupo, pra nunca
  // esconder onde o usuário está (ex.: dar F5 em /ajuste-grade já mostra o
  // grupo "Grade" aberto). Fora isso, abre/fecha é escolha do usuário (só
  // reage a MUDANÇA de rota, não fecha sozinho se ele abrir outro grupo).
  const ROTAS_POR_GRUPO = {
    grade: ['/grade-turmas', '/grade', '/ajuste-grade', '/turmas', '/professores'],
    'aluno-matricula': [
      '/gerenciar-matriculas',
      '/bolsistas',
      '/termos',
      '/listas',
      '/devolucoes',
    ],
    'servico-social': ['/assistencia-social'],
    seguranca: ['/admin-usuarios', '/permissoes'],
  };
  const grupoDaRota = (pathname) =>
    Object.entries(ROTAS_POR_GRUPO).find(([, rotas]) => rotas.includes(pathname))?.[0];
  const [gruposAbertos, setGruposAbertos] = useState(() => {
    const inicial = grupoDaRota(location.pathname);
    return inicial ? new Set([inicial]) : new Set();
  });
  useEffect(() => {
    const grupo = grupoDaRota(location.pathname);
    if (grupo)
      setGruposAbertos((prev) => (prev.has(grupo) && prev.size === 1 ? prev : new Set([grupo])));
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  // Só um grupo aberto por vez — abrir um fecha qualquer outro que já
  // estivesse aberto, pra não empilhar vários submenus expandidos ao mesmo
  // tempo (poluía a tela, especialmente no mobile onde o menu já é mais
  // apertado).
  const toggleGrupo = (id) =>
    setGruposAbertos((prev) => (prev.has(id) ? new Set() : new Set([id])));

  // "Trocar Unidade": limpa só a instituição selecionada, mantendo o login —
  // volta pra tela de seleção sem precisar entrar com e-mail/senha de novo.
  const sair = () => {
    selectInstitution(null);
    navigate('/selecionar');
  };

  // "Sair": logout completo (limpa token e instituição), volta pra tela de login.
  const sairDoSistema = () => {
    logout();
    selectInstitution(null);
    navigate('/login');
  };

  // Cada item pode restringir quem o vê de duas formas:
  // - `tela` — DINÂMICO: mesma checagem de telas liberadas na instituição
  //   ativa (`permissoes.telas_permitidas`) usada em PerfilRoute (App.js),
  //   editável pelo master na tela "Permissões".
  // - `perfis` — FIXO no código (não editável): só pras 2 telas com
  //   redirecionamento hardcoded pra master (Admin Usuários, Histórico) e pra
  //   ações que não são uma tela de verdade (Trocar Unidade).
  // Sem nenhuma das duas, o item aparece pra todo mundo (Trocar Senha, Sair).
  const menuItemsBase = [
    // Painel comparativo entre TODAS as instituições (ex-"Estatísticas") fica
    // acima de tudo — inclusive do "Painel do Gestor" por instituição logo
    // abaixo — por ser a visão mais ampla, agregada. "INH" no lugar do nome
    // de uma instituição específica.
    {
      label: 'PAINEL DO GESTOR - INH',
      icon: BarChart3,
      path: '/estatisticas-comparativas',
      action: () => navigate('/estatisticas-comparativas'),
      tela: '/estatisticas-comparativas',
    },
    // "Painel do Gestor" (ex-"Relatórios") fica de propósito no topo da lista
    // — quem tem acesso (master/coordenador via `telas_permitidas`, ver
    // PerfilRoute em App.js) já abre o sistema com a visão gerencial à mão,
    // antes de qualquer outro item de menu. Nome leva o da instituição ativa
    // na frente (ex.: "PAINEL DO GESTOR - Conexão Sertão").
    {
      label: institutionName ? `PAINEL DO GESTOR - ${institutionName}` : 'PAINEL DO GESTOR',
      icon: LayoutDashboard,
      path: '/relatorio-diario',
      action: () => navigate('/relatorio-diario'),
      tela: '/relatorio-diario',
    },
    { label: 'Chamada', icon: UserCheck, path: '/', action: () => navigate('/'), tela: '/' },
    {
      label: 'Notificações',
      icon: Bell,
      path: '/notificacoes',
      action: () => navigate('/notificacoes'),
      tela: '/notificacoes',
    },
    // "Grade" agrupa as 4 telas de grade/turma num item só com submenu —
    // item pai puramente organizacional (sem `path`/`action`/`tela` própria,
    // mesmo padrão de "Aluno e Matrícula" logo abaixo): clicar nele só
    // expande/recolhe, nunca navega sozinho. "Grade por Turma" (visão geral)
    // virou o filho "Atividades" em vez do destino do próprio pai. Cada tela
    // continua com sua própria rota e permissão (`tela=`) de sempre — só a
    // APRESENTAÇÃO no menu mudou, não dá pra vazar acesso: um perfil só vê
    // aqui os filhos que já tinha liberado antes (ver resolução de
    // `menuItems` mais abaixo). Se um perfil só tiver 1 dessas 4 telas
    // liberada, vira link direto (sem grupo/seta) em vez de um "grupo" de
    // uma coisa só.
    {
      id: 'grade',
      label: 'Grade',
      icon: LayoutGrid,
      children: [
        {
          label: 'Atividades',
          icon: LayoutGrid,
          path: '/grade-turmas',
          action: () => navigate('/grade-turmas'),
          tela: '/grade-turmas',
        },
        {
          label: 'Educador',
          icon: GraduationCap,
          path: '/grade',
          action: () => navigate('/grade'),
          tela: '/grade',
        },
        {
          label: 'Colunas',
          icon: Calendar,
          path: '/ajuste-grade',
          action: () => navigate('/ajuste-grade'),
          tela: '/ajuste-grade',
        },
        {
          label: 'Turmas',
          icon: Layers,
          path: '/turmas',
          action: () => navigate('/turmas'),
          tela: '/turmas',
        },
        {
          label: 'Educadores',
          icon: Contact,
          path: '/professores',
          action: () => navigate('/professores'),
          tela: '/professores',
        },
      ],
    },
    // Item puramente organizacional (sem `path`/`action`/`tela` própria) —
    // clicar nele só expande/recolhe, nunca navega sozinho, a pedido: as 4
    // telas que ele agrupa já existiam soltas no menu antes.
    {
      id: 'aluno-matricula',
      label: 'Aluno e Matrícula',
      icon: IdCard,
      children: [
        {
          label: 'Matrícula',
          icon: Users,
          path: '/gerenciar-matriculas',
          action: () => navigate('/gerenciar-matriculas'),
          tela: '/gerenciar-matriculas',
        },
        {
          label: 'Bolsistas',
          icon: GraduationCap,
          path: '/bolsistas',
          action: () => navigate('/bolsistas'),
          tela: '/bolsistas',
        },
        {
          label: 'Termos',
          icon: FileSignature,
          path: '/termos',
          action: () => navigate('/termos'),
          tela: '/termos',
        },
        {
          label: 'Listas',
          icon: ListChecks,
          path: '/listas',
          action: () => navigate('/listas'),
          tela: '/listas',
        },
        {
          label: 'Devolução de Materiais',
          icon: PackageCheck,
          path: '/devolucoes',
          action: () => navigate('/devolucoes'),
          tela: '/devolucoes',
        },
      ],
    },
    {
      id: 'servico-social',
      label: 'Serviço Social',
      icon: HeartHandshake,
      children: [
        {
          label: 'Acompanhamento Social',
          icon: Users,
          path: '/assistencia-social',
          action: () => navigate('/assistencia-social'),
          tela: '/assistencia-social',
        },
      ],
    },
    {
      label: 'Estoque',
      icon: Boxes,
      path: '/estoque',
      action: () => navigate('/estoque'),
      tela: '/estoque',
    },
    {
      label: 'Agenda',
      icon: CalendarX,
      path: '/dias-sem-aula',
      action: () => navigate('/dias-sem-aula'),
      tela: '/dias-sem-aula',
    },
    {
      label: 'Meritocracia',
      icon: Trophy,
      path: '/meritocracia',
      action: () => navigate('/meritocracia'),
      tela: '/meritocracia',
    },
    {
      label: 'Ocorrências',
      icon: AlertTriangle,
      path: '/ocorrencias',
      action: () => navigate('/ocorrencias'),
      tela: '/ocorrencias',
    },
    {
      label: 'Feedback Day',
      icon: CalendarCheck,
      path: '/feedback-day',
      action: () => navigate('/feedback-day'),
      tela: '/feedback-day',
    },
    {
      label: 'Presença Semanal',
      icon: Flame,
      path: '/foguinhos',
      action: () => navigate('/foguinhos'),
      tela: '/foguinhos',
    },
    {
      label: 'Formação de Caráter',
      icon: HeartHandshake,
      path: '/carater',
      action: () => navigate('/carater'),
      tela: '/carater',
    },
    {
      label: 'Notas',
      icon: ClipboardList,
      path: '/notas',
      action: () => navigate('/notas'),
      tela: '/notas',
    },
    {
      label: 'Planejamento',
      icon: BookOpen,
      path: '/planejamento',
      action: () => navigate('/planejamento'),
      tela: '/planejamento',
    },
    {
      label: 'Registro de Atividades',
      icon: Clock,
      path: '/pontos',
      action: () => navigate('/pontos'),
      tela: '/pontos',
    },
    {
      label: 'Vincular Educador',
      icon: Link2,
      path: '/vincular-professor',
      action: () => navigate('/vincular-professor'),
      tela: '/vincular-professor',
    },
    // Igual "Aluno e Matrícula": sem `tela`/`perfis` própria, só expande/
    // recolhe, nunca navega sozinho.
    {
      id: 'seguranca',
      label: 'Segurança',
      icon: Shield,
      children: [
        {
          label: 'Usuários',
          icon: UserCog,
          path: '/admin-usuarios',
          action: () => navigate('/admin-usuarios'),
          tela: '/admin-usuarios',
        },
        {
          label: 'Permissões',
          icon: ShieldCheck,
          path: '/permissoes',
          action: () => navigate('/permissoes'),
          tela: '/permissoes',
        },
      ],
    },
    {
      label: 'Áreas',
      icon: Tag,
      path: '/areas',
      action: () => navigate('/areas'),
      perfis: ['master'],
    },
    {
      label: 'Histórico',
      icon: History,
      path: '/historico-aluno',
      action: () => navigate('/historico-aluno'),
      perfis: ['master', 'coordenador'],
    },
    {
      label: 'Recursos do Sistema',
      icon: Server,
      path: '/recursos-sistema',
      action: () => navigate('/recursos-sistema'),
      perfis: ['master'],
    },
    {
      label: 'Trocar Senha',
      icon: Lock,
      path: '/trocar-senha',
      action: () => navigate('/trocar-senha'),
    },
    {
      label: 'Trocar Unidade',
      icon: ArrowRightLeft,
      action: sair,
      perfis: ['master', 'coordenador'],
    },
    { label: 'Sair', icon: LogOut, action: sairDoSistema, danger: true },
  ];
  const podeTela = (tela) =>
    user?.perfil === 'master' || permissoes?.telas_permitidas?.includes(tela);
  // Mesma checagem usada pros itens soltos de sempre (`tela` dinâmica OU
  // `perfis` fixo no código) — extraída pra função porque agora também
  // precisa rodar em cima de FILHOS de grupo (ex.: "Admin Usuários" e
  // "Permissões" dentro de "Segurança" usam `perfis`, não `tela`).
  const podeItem = (item) => {
    if (item.label === 'Trocar Unidade') {
      return (
        user?.perfil === 'master' ||
        ['master', 'coordenador'].includes(user?.perfil) ||
        (Array.isArray(user?.instituicoes) && user.instituicoes.length > 1)
      );
    }
    return item.tela ? podeTela(item.tela) : !item.perfis || item.perfis.includes(user?.perfil);
  };
  const menuItems = menuItemsBase
    .filter((item) => {
      if (item.children) return true; // grupo: resolvido no passo seguinte, com os filhos já filtrados
      return podeItem(item);
    })
    .map((item) => {
      if (!item.children) return item;
      // `!!item.tela` importa aqui: um grupo puramente organizacional (ex.:
      // "Aluno e Matrícula"/"Segurança", sem tela própria) não pode virar
      // "destino clicável" só porque o usuário é master — master passa em
      // `podeTela` pra QUALQUER string, inclusive `undefined`, então sem
      // essa checagem o grupo ganharia uma navegação própria que não
      // deveria existir.
      const podeProprio = !!item.tela && podeTela(item.tela);
      const filhos = item.children.filter(podeItem);
      const totalDestinos = (podeProprio ? 1 : 0) + filhos.length;
      if (totalDestinos === 0) return null;
      // Só 1 opção liberada nesse grupo: mostra ela direto, sem grupo/seta.
      if (totalDestinos === 1) {
        if (podeProprio) {
          const { children, ...unico } = item;
          return unico;
        }
        return filhos[0];
      }
      return { ...item, children: filhos, podeProprio };
    })
    .filter(Boolean);

  const pageTitles = {
    '/': 'Chamada',
    '/grade': 'Grade — Educador',
    '/ajuste-grade': 'Grade — Colunas',
    '/grade-turmas': 'Grade — Atividades',
    '/turmas': 'Grade — Turmas',
    '/professores': 'Grade — Educadores',
    '/gerenciar-matriculas': 'Aluno e Matrícula — Matrícula',
    '/bolsistas': 'Aluno e Matrícula — Bolsistas',
    '/assistencia-social': 'Serviço Social — Acompanhamento',
    '/dias-sem-aula': 'Agenda',
    '/meritocracia': 'Meritocracia',
    '/carater': 'Formação de Caráter',
    '/notas': 'Notas',
    '/planejamento': 'Planejamento Semanal',
    '/pontos': 'Registro de Atividades',
    '/admin-usuarios': 'Segurança — Admin Usuários',
    '/permissoes': 'Segurança — Permissões',
    '/listas': 'Aluno e Matrícula — Listas',
    '/termos': 'Aluno e Matrícula — Termos',
    '/devolucoes': 'Aluno e Matrícula — Devolução de Materiais',
    '/vincular-professor': 'Vincular Educador',
    '/historico-aluno': 'Histórico',
    '/estatisticas-comparativas': 'PAINEL DO GESTOR - INH',
    '/recursos-sistema': 'Recursos do Sistema',
    '/trocar-senha': 'Trocar Senha',
    '/notificacoes': 'Notificações',
    '/foguinhos': 'Presença Semanal',
  };

  // "Painel do Gestor" (por instituição) não entra em pageTitles porque seu
  // título depende da instituição ativa — mesmo texto usado no item de menu
  // acima (ver menuItemsBase).
  const title =
    location.pathname === '/relatorio-diario'
      ? institutionName
        ? `PAINEL DO GESTOR - ${institutionName}`
        : 'PAINEL DO GESTOR'
      : pageTitles[location.pathname] || institutionName || 'Portal Novas Histórias';
  // Essas duas rotas já embutem a instituição (ou "INH") no próprio título —
  // ver comentário junto do <h2> abaixo.
  const ocultarSufixoInstituicao =
    location.pathname === '/relatorio-diario' || location.pathname === '/estatisticas-comparativas';
  const showBack = location.pathname !== '/';
  // Header compacto (~44%, h-16 -> h-9, título numa linha só) — testado
  // primeiro só em Ajuste Grade (planilha densa, precisava do espaço
  // vertical de volta) e depois adotado em todas as telas, por padrão.
  const headerCompacto = true;

  // Chute inicial pelo relógio do aparelho, só pra não deixar o cabeçalho em
  // branco no primeiro render — corrigido pela data do SERVIDOR logo abaixo,
  // mesmo padrão de App.js/Chamada (GET /api/hoje): se o relógio/fuso do
  // aparelho estiver errado, o cabeçalho não fica preso nele.
  const [dataServidor, setDataServidor] = useState(() => {
    const d = new Date();
    const offset = d.getTimezoneOffset();
    return new Date(d.getTime() - offset * 60000).toISOString().split('T')[0];
  });

  useEffect(() => {
    if (!token) return;
    fetch(`${API_URL}/api/hoje`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then((body) => {
        const data = body?.data ?? body;
        if (data?.hoje) setDataServidor(data.hoje);
      })
      .catch(() => {});
  }, [token]);

  // Formata "YYYY-MM-DD" via Date.UTC + timeZone: 'UTC', nunca `new
  // Date(string)` direto — evitaria o navegador reler em fuso local e
  // deslocar o dia perto da meia-noite (mesmo cuidado de formatarDataBR em
  // utils/data.js).
  const [anoServidor, mesServidor, diaServidor] = dataServidor.split('-').map(Number);
  const todayLabel = new Date(
    Date.UTC(anoServidor, mesServidor - 1, diaServidor),
  ).toLocaleDateString('pt-BR', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    timeZone: 'UTC',
  });

  return (
    <div className="flex h-screen w-full bg-[#F9FAFB] dark:bg-gray-950 overflow-hidden">
      {menuAberto && (
        <div
          className="fixed inset-0 bg-black/20 z-30 md:hidden"
          onClick={() => setMenuAberto(false)}
        />
      )}

      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 ${menuColapsado ? 'md:w-20' : ''} flex-shrink-0 flex flex-col bg-white/65 dark:bg-gray-900/80 backdrop-blur-xl border-r border-gray-200/60 dark:border-gray-800/60 transform-gpu transition-all duration-300 ${
          menuAberto ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div
          className={`p-6 flex items-center gap-3 ${menuColapsado ? 'md:justify-center md:px-3' : ''}`}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600 flex items-center justify-center shadow-sm flex-shrink-0">
            <GraduationCap className="w-6 h-6 text-white" />
          </div>
          <div className={`flex-1 min-w-0 ${menuColapsado ? 'md:hidden' : ''}`}>
            <h1
              className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-tight"
              translate="no"
            >
              {institutionName || 'Controle de Presença'}
            </h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">Portal Novas Histórias</p>
          </div>
          <button
            onClick={() => setMenuAberto(false)}
            className="md:hidden w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors"
            aria-label="Fechar menu"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div
          className={`hidden md:flex px-3 pb-2 ${menuColapsado ? 'justify-center' : 'justify-end'}`}
        >
          <button
            onClick={alternarMenuColapsado}
            className="w-8 h-8 rounded-lg hover:bg-gray-200/50 dark:hover:bg-gray-800/50 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            aria-label={menuColapsado ? 'Expandir menu' : 'Reduzir menu'}
            title={menuColapsado ? 'Expandir menu' : 'Reduzir menu'}
          >
            {menuColapsado ? (
              <PanelLeftOpen className="w-4 h-4" />
            ) : (
              <PanelLeftClose className="w-4 h-4" />
            )}
          </button>
        </div>

        <div className="px-3 pb-2">
          {user && (
            <div
              className={`flex items-center gap-3 px-3 py-2.5 rounded-xl bg-gray-100/60 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-800 ${menuColapsado ? 'md:justify-center md:px-0' : ''}`}
            >
              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center text-blue-600 dark:text-blue-400 text-xs font-semibold flex-shrink-0">
                {user.nome?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className={`min-w-0 ${menuColapsado ? 'md:hidden' : ''}`}>
                <p
                  className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate"
                  translate="no"
                >
                  {user.nome}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 capitalize">{user.perfil}</p>
              </div>
            </div>
          )}
        </div>

        <nav
          ref={navRef}
          onScroll={(e) => {
            ultimaRolagemMenu = e.currentTarget.scrollTop;
          }}
          className="flex-1 px-3 py-4 space-y-1 overflow-y-auto"
        >
          {menuItems.map((item, idx) => {
            if (item.children) {
              return (
                <MenuGrupo
                  key={idx}
                  item={item}
                  aberto={gruposAbertos.has(item.id)}
                  onToggle={() => toggleGrupo(item.id)}
                  menuColapsado={menuColapsadoEfetivo}
                  location={location}
                  onNavegar={(acao) => {
                    acao();
                    if (window.innerWidth < 768) setMenuAberto(false);
                  }}
                />
              );
            }
            const Icon = item.icon;
            const isActive = item.path && location.pathname === item.path;
            const base = `w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${menuColapsado ? 'md:justify-center md:px-0 md:gap-0' : ''}`;
            const style = item.danger
              ? 'text-red-600 dark:text-red-400 hover:bg-red-50/80 dark:hover:bg-red-950/40'
              : isActive
                ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100/80 dark:hover:bg-blue-900/50'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100';
            return (
              <button
                key={idx}
                onClick={() => {
                  item.action();
                  if (window.innerWidth < 768) setMenuAberto(false);
                }}
                className={`${base} ${style}`}
                title={item.label}
                translate="no"
              >
                <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                {/* Nomes de instituição compridos (ex.: "PAINEL DO GESTOR - <nome>")
                    quebram em várias linhas em vez de cortar com "..." — o
                    nome precisa aparecer inteiro, nunca só o começo. */}
                <span
                  className={`text-left leading-tight whitespace-normal break-words ${menuColapsado ? 'md:hidden' : ''}`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </nav>

        <div className="p-4 border-t border-gray-200/60 dark:border-gray-800/60">
          <div
            className={`flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 ${menuColapsado ? 'md:justify-center' : ''}`}
          >
            <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
            <span className={menuColapsado ? 'md:hidden' : ''}>Online</span>
          </div>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* transform-gpu + translate-x-0 (0px, sem efeito visual): força o navegador a
            manter esse header com backdrop-blur numa camada de composição própria.
            Sem isso, alguns navegadores só repintam o desfoque depois de um scroll,
            fazendo o conteúdo logo abaixo (ex.: botões da tela de Matrículas) sumir
            até o usuário rolar a página. */}
        <header
          className={`relative z-30 flex items-center justify-between border-b border-gray-200/60 dark:border-gray-800/60 bg-white/50 dark:bg-gray-900/60 backdrop-blur-md shrink-0 transform-gpu translate-x-0 transition-[height] ${headerCompacto ? 'h-11 px-2 md:h-9 md:px-3' : 'h-16 px-4'}`}
        >
          <div className={`flex items-center ${headerCompacto ? 'gap-1 md:gap-2' : 'gap-3'}`}>
            {/* Só existe no mobile (md:hidden) — tamanho de toque fixo em
                44x44 (mínimo recomendado pela Apple/iOS), sem depender de
                headerCompacto: antes ficava do mesmo tamanho reduzido do
                cabeçalho no desktop (24px), pequeno demais pra tocar bem no
                celular. */}
            <button
              onClick={() => setMenuAberto(true)}
              className="md:hidden w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors flex-shrink-0"
              aria-label="Abrir menu"
            >
              <Menu className="w-6 h-6" />
            </button>
            {showBack && (
              <button
                onClick={() => navigate(-1)}
                className={`rounded-lg bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-300 transition-colors flex-shrink-0 w-11 h-11 md:w-8 md:h-8`}
                aria-label="Voltar"
              >
                <ArrowLeft className="w-5 h-5 md:w-4 md:h-4" />
              </button>
            )}
            {headerCompacto ? (
              <h2
                className="text-xs font-semibold text-gray-900 dark:text-gray-100 leading-tight"
                translate="no"
              >
                {title}
                {/* "PAINEL DO GESTOR - <instituição>"/"- INH" já leva o nome
                    no próprio título — repetir "· instituição" aqui duplicaria
                    (ou, no caso do INH, colaria errado o nome de uma
                    instituição específica numa tela que é agregada). */}
                {!ocultarSufixoInstituicao && (
                  <span className="font-normal text-gray-400 dark:text-gray-500">
                    {' '}
                    · {institutionName || 'Portal Novas Histórias'}
                  </span>
                )}
              </h2>
            ) : (
              <div>
                <h2
                  className="text-lg font-semibold text-gray-900 dark:text-gray-100"
                  translate="no"
                >
                  {title}
                </h2>
                {!ocultarSufixoInstituicao && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {institutionName || 'Portal Novas Histórias'}
                  </p>
                )}
              </div>
            )}
          </div>
          <div className={`flex items-center ${headerCompacto ? 'gap-2' : 'gap-3'}`}>
            <div
              className={`hidden sm:flex items-center gap-1.5 text-gray-500 dark:text-gray-400 ${headerCompacto ? 'text-xs' : 'text-sm gap-2'}`}
            >
              <Calendar className={headerCompacto ? 'w-3.5 h-3.5' : 'w-4 h-4'} />
              <span>{todayLabel}</span>
            </div>
            {['master', 'coordenador', 'professor'].includes(user?.perfil) && (
              <NotificationBell size={headerCompacto ? 'sm' : 'md'} />
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// Item de menu com submenu — reutilizado por qualquer grupo em
// menuItemsBase (hoje: "Grade" e "Aluno e Matrícula"). Se o grupo tem tela
// própria (`item.tela`, ex.: "Grade" abre Grade por Turma), o clique no
// título também navega; grupo puramente organizacional (sem `tela`, ex.:
// "Aluno e Matrícula") só expande/recolhe, nunca navega sozinho — ver
// `podeProprio` na resolução de `menuItems`. No modo normal expande/recolhe
// inline, empurrando os itens de baixo (igual um accordion comum). No modo
// colapsado (só ícones) não dá pra expandir inline (não tem espaço pro texto
// dos filhos), então vira um popover flutuante ao lado do ícone — fecha ao
// clicar fora, mesmo padrão de FiltroColunaSimples em Professores.js.
function MenuGrupo({ item, aberto, onToggle, menuColapsado, location, onNavegar }) {
  const [popoverAberto, setPopoverAberto] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!popoverAberto) return;
    const fecharSeFora = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setPopoverAberto(false);
    };
    document.addEventListener('mousedown', fecharSeFora);
    return () => document.removeEventListener('mousedown', fecharSeFora);
  }, [popoverAberto]);

  const Icon = item.icon;
  const grupoAtivo =
    location.pathname === item.path || item.children.some((c) => c.path === location.pathname);

  const destinos = [
    ...(item.podeProprio
      ? [
          {
            label: item.label,
            icon: item.icon,
            path: item.path,
            action: item.action,
            principal: true,
          },
        ]
      : []),
    ...item.children,
  ];

  if (menuColapsado) {
    return (
      <div className="relative md:flex md:justify-center" ref={ref}>
        <button
          onClick={() => setPopoverAberto((a) => !a)}
          title={item.label}
          className={`w-full md:w-auto flex items-center gap-3 md:gap-0 md:justify-center px-3 md:px-0 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
            grupoAtivo
              ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 hover:bg-blue-100/80 dark:hover:bg-blue-900/50'
              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
          }`}
        >
          <Icon className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="md:hidden">{item.label}</span>
        </button>
        {popoverAberto && (
          <div className="absolute z-50 left-full md:left-16 top-0 ml-1 w-44 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg py-1.5">
            <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              {item.label}
            </p>
            {destinos.map((d, i) => {
              const DIcon = d.icon;
              const ativo = location.pathname === d.path;
              return (
                <button
                  key={i}
                  onClick={() => {
                    setPopoverAberto(false);
                    onNavegar(d.action);
                  }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left transition-colors ${
                    ativo
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  }`}
                  translate="no"
                >
                  <DIcon className="w-4 h-4 flex-shrink-0" />
                  {d.label}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div
        className={`w-full flex items-center gap-1 rounded-xl text-sm font-medium transition-all duration-200 ${
          grupoAtivo
            ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
        }`}
      >
        <button
          onClick={() => {
            onToggle();
            if (item.podeProprio) onNavegar(item.action);
          }}
          className="flex-1 flex items-center gap-3 px-3 py-2.5 text-left min-w-0"
          title={item.label}
          translate="no"
        >
          <Icon className="w-[18px] h-[18px] flex-shrink-0" />
          <span className="text-left leading-tight truncate">{item.label}</span>
        </button>
        <button
          onClick={onToggle}
          className="px-2.5 py-2.5 flex-shrink-0"
          aria-label={aberto ? 'Recolher submenu' : 'Expandir submenu'}
        >
          {aberto ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </button>
      </div>
      {aberto && (
        <div className="mt-1 ml-4 pl-3 border-l border-gray-200 dark:border-gray-800 space-y-1">
          {item.children.map((child, i) => {
            const ChildIcon = child.icon;
            const ativo = location.pathname === child.path;
            return (
              <button
                key={i}
                onClick={() => onNavegar(child.action)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  ativo
                    ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-200/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                }`}
                title={child.label}
                translate="no"
              >
                <ChildIcon className="w-4 h-4 flex-shrink-0" />
                <span className="truncate">{child.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Layout;
